import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ ignoreMobileResize: true });

const isMobileFade = () => window.matchMedia("(max-width: 768px)").matches;
const mobileFadeQuery = window.matchMedia("(max-width: 768px)");

const fadeScrollTrigger = (trigger) => ({
	trigger,
	start: () => isMobileFade() ? "top 88%" : "top 80%",
	end: () => isMobileFade() ? "bottom 12%" : "bottom 20%",
	invalidateOnRefresh: true,
	toggleActions: "play reverse play reverse",
});

const fadeFromVars = (axis, distance) => ({
	autoAlpha: 0,
	[axis]: distance,
	...(isMobileFade() ? {} : { filter: "blur(10px)" }),
});

const fadeToVars = (axis) => ({
	autoAlpha: 1,
	[axis]: 0,
	...(isMobileFade() ? {} : { filter: "blur(0px)" }),
});

const refreshFadeTriggers = () => {
	requestAnimationFrame(() => ScrollTrigger.refresh());
};

if (mobileFadeQuery.addEventListener) {
	mobileFadeQuery.addEventListener("change", refreshFadeTriggers);
} else {
	mobileFadeQuery.addListener(refreshFadeTriggers);
}

export const fadeDownWave = () => {
	const containers = document.querySelectorAll(".cw-fade-down-js");

	containers.forEach((container) => {
		const items = container.querySelectorAll(":scope > *");

		if (!items.length) return;

		gsap.fromTo(
			items,
			fadeFromVars("y", -24),
			{
				...fadeToVars("y"),
				duration: 0.8,
				ease: "power3.out",
				stagger: 0.12,
				scrollTrigger: {
					...fadeScrollTrigger(container),
				},
			}
		);
	});
};

export const fadeLeft = () => {
	const wrappers = document.querySelectorAll(".cw-fade-left-js");

	if (!wrappers.length) return;

	const mm = gsap.matchMedia();

	mm.add("(min-width: 992px)", () => {
		createFadeLeftAnimations(wrappers);
	});

	mm.add("(max-width: 991.98px)", () => {
		createFadeLeftAnimations(wrappers);
	});
};

const createFadeLeftAnimations = (wrappers) => {
	wrappers.forEach((wrapper) => {
		const items = wrapper.querySelectorAll(":scope > *");

		if (!items.length) return;

		gsap.set(items, {
			opacity: 0,
			x: 60,
		});

		gsap.to(items, {
			opacity: 1,
			x: 0,
			stagger: 0.15,
			ease: "none",
			scrollTrigger: {
				trigger: wrapper,
				start: "top 90%",
				end: "bottom 80%",
				scrub: true,
				invalidateOnRefresh: true,
			}
		});
	});
};

export const fadeUp = () => {
	const elements = document.querySelectorAll(".cw-fade-up-js");

	elements.forEach((el) => {
		gsap.fromTo(
			el,
			fadeFromVars("y", 20),
			{
				...fadeToVars("y"),
				duration: 0.8,
				ease: "power3.out",
				scrollTrigger: {
					...fadeScrollTrigger(el),
				},
			}
		);
	});
};

export const fadeUpLonger = () => {
	const headings = document.querySelectorAll("h1");
	const introElements = new Set(headings);

	headings.forEach((heading) => {
		let sibling = heading.nextElementSibling;

		while (sibling?.matches("h2, h3, p")) {
			introElements.add(sibling);
			sibling = sibling.nextElementSibling;
		}
	});

	const elements = new Set([
		...introElements,
		...document.querySelectorAll(".cw-fade-up-longer-js"),
	]);

	const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	if (reduceMotion) {
		elements.forEach((el) => {
			gsap.set(el, { clearProps: "transform,opacity" });
		});
		releaseIntroMotionState();
		return;
	}

	elements.forEach((el) => {
		const isIntroElement = introElements.has(el);

		gsap.fromTo(
			el,
			{
				y: 140,
				opacity: 0
			},
			{
				y: 0,
				opacity: 1,
				duration: 1.5,
				ease: "power3.out",
				...(isIntroElement ? {} : {
					scrollTrigger: {
						...fadeScrollTrigger(el),
					},
				}),
			}
		);
	});

	// GSAP now owns the initial inline state, so releasing the CSS state cannot flash.
	releaseIntroMotionState();
};

const releaseIntroMotionState = () => {
	window.clearTimeout(window.cwIntroMotionFallback);
	document.documentElement.classList.remove("cw-intro-motion");
};
